import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles, withStyles } from '@mui/styles';
import {
  primaryColor,
  secondaryColor,
  ThemedCheckbox,
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
  whiteColor,
} from '@themes/defaultThemeProps';
import commonStyle from 'assets/jss/material-dashboard-pro-react/commonStyle';
import CustomInput from 'components/CustomInput/CustomInput.js';
import RangeNumberInput from 'components/CustomInput/RangeNumberInput.js';
import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { useGetTenantQuery } from 'api/tenant.api';
import { getCurrencySymbol } from '@helpers/currency';

const useStyles = makeStyles((theme) => ({
  ...commonStyle,
  root: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  parameterName: {
    fontWeight: 500,
  },
  immutableLabel: {
    fontWeight: 500,
  },
}));

const integerMaskOptions = {
  prefix: '',
  suffix: '',
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowDecimal: false,
  decimalSymbol: '.',
  decimalLimit: 0, // how many digits allowed after the decimal
  integerLimit: 7, // limit length of integer numbers
  allowNegative: false,
  allowLeadingZeroes: false,
};

const integerMask = createNumberMask({
  ...integerMaskOptions,
});

const integerMaskProps = {
  guide: false,
  placeholderChar: '\u2000',
  mask: integerMask,
};

const integerMaskCustom = (props) => {
  let { inputRef, ...other } = props;

  if (typeof inputRef !== 'function') {
    inputRef = () => {};
  }

  return (
    <MaskedInput
      {...other}
      {...integerMaskProps}
      ref={(ref) => {
        inputRef(ref ? ref.inputElement : null);
      }}
    />
  );
};

export default function ParameterEditor(props) {
  const dispatch = useDispatch();
  const { data: tenant } = useGetTenantQuery();

  const parameter = props.parameter;

  const [parameterValue, setParameterValue] = useState(parameter.value);
  const [immutable, setImmutable] = useState(parameter.immutable);

  const title = 'Edit Parameter Setting';
  const submitLabel = 'Save Changes';

  const classes = useStyles();

  // Create dynamic currency mask based on tenant's base currency
  const currencyMaskOptions = useMemo(() => ({
    prefix: '',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2, // how many digits allowed after the decimal
    integerLimit: 7, // limit length of integer numbers
    allowNegative: false,
    allowLeadingZeroes: false,
  }), [tenant?.base_currency]);

  const currencyMask = useMemo(() => createNumberMask({
    ...currencyMaskOptions,
  }), [currencyMaskOptions]);

  const currencyMaskProps = useMemo(() => ({
    guide: false,
    placeholderChar: '\u2000',
    mask: currencyMask,
  }), [currencyMask]);

  const currencyMaskCustom = useMemo(() => (props) => {
    let { inputRef, ...other } = props;

    if (typeof inputRef !== 'function') {
      inputRef = () => {};
    }

    return (
      <MaskedInput
        {...other}
        {...currencyMaskProps}
        ref={(ref) => {
          inputRef(ref ? ref.inputElement : null);
        }}
      />
    );
  }, [currencyMaskProps]);

  const buildSelectOptions = () => {
    if (parameter.type === 'Selection') {
      const options = { None: false };

      parameter.options.forEach((curr) => {
        options[curr] = false;
      });

      let currentOptions = parameter.value.split(',');

      currentOptions.forEach((option) => {
        if (options.hasOwnProperty(option)) {
          options[option] = true;
        }
      });

      options['None'] =
        currentOptions.length === 0 || currentOptions.includes('None');

      return options;
    } else {
      return null;
    }
  };

  const [selectOptions, setSelectOptions] = React.useState(buildSelectOptions);

  const handleCheckboxChange = (event) => {
    let updatedSelectOptions = {
      ...selectOptions,
      [event.target.name]: event.target.checked,
    };

    updatedSelectOptions[event.target.name] = event.target.checked;

    const selectedOptionsCount = Object.keys(updatedSelectOptions).filter(
      (option) => updatedSelectOptions[option] && option !== 'None'
    ).length;

    if (selectedOptionsCount === 0) {
      updatedSelectOptions['None'] = true;
    }

    setParameterValue(
      Object.keys(updatedSelectOptions)
        .filter((option) => updatedSelectOptions[option])
        .join(',')
    );
    setSelectOptions(updatedSelectOptions);
  };

  const stripFormatting = (stringType, formattedString) => {
    // scrub any unnecessary formatting
    var strippedString = '';
    if (stringType === 'Currency') {
      // Remove the currency symbol (whatever it is)
      const currencySymbol = getCurrencySymbol(tenant?.base_currency || 'USD');
      strippedString = formattedString.replaceAll(currencySymbol, ''); // remove the leading currency symbol
      strippedString = strippedString.replaceAll(',', ''); // remove all commas
    } else strippedString = formattedString;
    return strippedString;
  };

  // eslint-disable-next-line
  const handleUpdate = (e) => {
    e.preventDefault();

    const updatedParameter = {
      ...parameter,
      value: stripFormatting(parameter.type, parameterValue),
      immutable: immutable,
    };
    props.handleUpdate(updatedParameter);

    props.handleClose();
  };

  const handleSubmit = handleUpdate;

  const AntSwitch = withStyles(() => ({
    root: {
      width: 28,
      height: 16,
      padding: 0,
      display: 'flex',
    },
    switchBase: {
      padding: 2,
      color: secondaryColor[1],
      '&$checked': {
        transform: 'translateX(12px)',
        color: whiteColor,
        '& + $track': {
          opacity: 1,
          backgroundColor: primaryColor[0],
          borderColor: primaryColor[0],
        },
      },
    },
    thumb: {
      width: 12,
      height: 12,
      boxShadow: 'none',
    },
    track: {
      border: `1px solid ${primaryColor[0]}`,
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: whiteColor,
    },
    checked: {},
  }))(Switch);

  const SelectionOptions = () => {
    const options = () => {
      return Object.keys(selectOptions).map((option, key) => (
        <FormControlLabel
          control={
            <ThemedCheckbox
              checked={selectOptions[option]}
              onChange={handleCheckboxChange}
              name={option}
            />
          }
          label={option}
          key={key}
        />
      ));
    };
    return (
      (<FormControl variant="standard" component='fieldset' className={classes.formControl}>
        <FormLabel component='legend'>Choose options</FormLabel>
        {options()}
      </FormControl>)
    );
  };

  const renderMaxQuickPayCashInput = () => (
    <RangeNumberInput
      id='integerdefault'
      value={parameterValue}
      min={1}
      max={9}
      onChange={setParameterValue}
    />
  );

  const parameterValueEditor = () => {
    switch (parameter.type) {
      case 'String':
        return (
          (<TextField
            variant="standard"
            className={classes.formControl}
            autoFocus
            margin='dense'
            id='value'
            label='Value'
            type='text'
            defaultValue={parameterValue}
            onChange={(e) => setParameterValue(e.target.value)} />)
        );
      case 'Date':
        break;
      case 'DateRange':
        break;
      case 'Integer':
        if (parameter.name === 'Maximum_QuickPay_Cash_Buttons') {
          return renderMaxQuickPayCashInput();
        }
        return (
          <CustomInput
            id='integerdefault'
            inputProps={{
              defaultValue: parameterValue,
              onChange: (e) => {
                setParameterValue(e.target.value);
              },
              inputComponent: integerMaskCustom,
              autoComplete: 'off',
            }}
          />
        );
      case 'Currency':
        return (
          <CustomInput
            id='default'
            inputProps={{
              defaultValue: parameterValue,
              onChange: (e) => {
                setParameterValue(e.target.value);
              },
              inputComponent: currencyMaskCustom,
              autoComplete: 'off',
            }}
          />
        );
      case 'Decimal':
        break;
      case 'Boolean':
        return (
          <Typography component='div' className={classes.formControl}>
            <Grid component='label' container alignItems='center' spacing={2}>
              <Grid item>False</Grid>
              <Grid item>
                <AntSwitch
                  id='booleanValue'
                  label='booleanValue'
                  checked={parameterValue === 'true' || parameterValue === true}
                  onChange={(e) =>
                    setParameterValue(e.target.checked.toString())
                  }
                />
              </Grid>
              <Grid item>True</Grid>
            </Grid>
          </Typography>
        );
      case 'Choice':
        return (
          (<FormControl variant="standard" className={classes.formControl}>
            <InputLabel id='value-select-label' className={classes.inputLabel}>
              Value
            </InputLabel>
            <Select
              variant="standard"
              className={classes.select}
              labelId='value-select-label'
              id='value-select'
              value={parameterValue}
              onChange={(e) => setParameterValue(e.target.value)}>
              {parameter.options.map((option, key) => {
                return (
                  <MenuItem value={option} key={key}>
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>)
        );
      case 'Selection':
        return <SelectionOptions />;
      default:
        break;
    }
  };

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby='form-dialog-title'
      >
        <DialogTitle id='form-dialog-title'>{title}</DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} className={classes.parameterName}>
              {parameter.name}
            </Grid>
            <Grid item xs={12}>
              {parameter.description}
            </Grid>
            <Grid item xs={12}>
              {parameterValueEditor()}
            </Grid>
            <Grid item xs={12}>
              <span className={classes.parameterName}>Immutable?</span>
              <Typography component='div' className={classes.formControl}>
                <Grid
                  component='label'
                  container
                  alignItems='center'
                  spacing={2}
                >
                  <Grid item>False</Grid>
                  <Grid item>
                    <AntSwitch
                      id='immutable'
                      label='immutable'
                      checked={immutable === 'true' || immutable === true}
                      onChange={(e) => setImmutable(e.target.checked)}
                    />
                  </Grid>
                  <Grid item>True</Grid>
                </Grid>
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={props.handleClose} color='secondary'>
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn
            onClick={handleSubmit}
            color='primary'
          >
            {submitLabel}
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
